import React, { useEffect, useState } from 'react';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import {
  ArtefactCitation,
  CaseArtefact,
  CaseTagSuggestion,
  AcceptCaseTagSuggestionRequest,
  RejectCaseTagSuggestionRequest,
} from '../types';
import {
  buildDashboardModel,
  DashboardQuestion,
  DashboardTabId,
  DashboardTimelineItem,
} from '../utils/dashboardModel';
import { canRenderCitation, mergeArtefactCitations } from '../utils/citations';
import styles from './CaseIntelligenceDashboard.module.scss';
import TagSuggestionsPanel from './TagSuggestionsPanel';
import CaseIntelligenceCitationList from './CaseIntelligenceCitationList';

interface Props {
  artefacts: CaseArtefact[];
  claimantName?: string;
  respondentName?: string;
  tagSuggestions?: CaseTagSuggestion[] | null;
  tagSuggestionsPending?: boolean;
  tagSuggestionsFeedback?: { error?: unknown } | null;
  onRetryTags?: () => void;
  onAcceptTagSuggestion?: (payload: AcceptCaseTagSuggestionRequest) => Promise<unknown> | unknown;
  onRejectTagSuggestion?: (payload: RejectCaseTagSuggestionRequest) => Promise<unknown> | unknown;
  onCitationClick?: (citation: ArtefactCitation) => void;
  activeCitationSignature?: string | null;
  activeTab?: DashboardUiTabId | null;
  onActiveTabChange?: (tab: DashboardUiTabId | null) => void;
  issueFilter?: IssueFilter;
  onIssueFilterChange?: (filter: IssueFilter) => void;
}

type DashboardUiTabId = DashboardTabId | 'tags';
type IssueFilter = 'all' | 'legal' | 'theme';

const TAGS_TAB = { id: 'tags' as const, label: 'Tags' };
const PRIORITY_LABELS: Record<DashboardQuestion['priority'], string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};
const ISSUE_FILTERS: Array<{ id: IssueFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'legal', label: 'Legal issues' },
  { id: 'theme', label: 'Narrative themes' },
];

function formatCount(value: number, singular: string, plural: string) {
  return `${value} ${value === 1 ? singular : plural}`;
}

function humanizeToken(value: string | null | undefined) {
  if (!value) {
    return null;
  }

  return value
    .split(/[_-]/g)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

function formatRoleLine(role: string | null | undefined, type: string | null | undefined) {
  return [humanizeToken(role), humanizeToken(type)].filter(Boolean).join(' · ');
}

function formatMentionCount(count: number | null | undefined) {
  if (count == null) {
    return null;
  }

  return `Mentioned in ${formatCount(count, 'document', 'documents')}`;
}

function formatDateLabel(value: string) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T00:00:00Z`).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  if (/^\d{4}-\d{2}$/.test(value)) {
    return new Date(`${value}-01T00:00:00Z`).toLocaleDateString('en-GB', {
      month: 'long',
      year: 'numeric',
    });
  }

  if (/^\d{4}$/.test(value)) {
    return value;
  }

  return value;
}

function buildTimelineDescription(item: DashboardTimelineItem) {
  return item.description || null;
}

function renderTextWithCitations(
  text: string | null | undefined,
  citations?: ArtefactCitation[] | null,
  onCitationClick?: (citation: ArtefactCitation) => void,
  activeCitationSignature?: string | null,
) {
  if (!text) {
    return null;
  }

  return (
    <>
      {text}
      <CaseIntelligenceCitationList
        citations={citations}
        onCitationClick={onCitationClick}
        activeCitationSignature={activeCitationSignature}
      />
    </>
  );
}

function renderTextWithoutCitations(text: string | null | undefined) {
  return text || null;
}

function renderSectionCitations(
  citations?: ArtefactCitation[] | null,
  onCitationClick?: (citation: ArtefactCitation) => void,
  activeCitationSignature?: string | null,
) {
  if (!citations?.some((citation) => canRenderCitation(citation))) {
    return null;
  }

  return (
    <div className={styles.sectionCitations}>
      <CaseIntelligenceCitationList
        citations={citations}
        onCitationClick={onCitationClick}
        activeCitationSignature={activeCitationSignature}
      />
    </div>
  );
}

function getColumnDotClass(
  side: 'claimant' | 'respondent' | 'non_aligned',
  stylesMap: Record<string, string>,
) {
  switch (side) {
    case 'claimant':
      return stylesMap.columnDotClaimant;
    case 'respondent':
      return stylesMap.columnDotRespondent;
    default:
      return stylesMap.columnDotNonAligned;
  }
}

function getPartyDotClass(
  value: string | null | undefined,
  stylesMap: Record<string, string>,
) {
  const normalizedValue = value?.trim().toLowerCase() || '';

  if (/(claimant|plaintiff)/.test(normalizedValue)) {
    return stylesMap.partyDotClaimant;
  }

  if (/(respondent|defendant)/.test(normalizedValue)) {
    return stylesMap.partyDotRespondent;
  }

  return '';
}

function ReadOnlyActionIcons() {
  return (
    <div className={styles.readOnlyActions} aria-hidden="true">
      <span className={`${styles.actionIcon} ${styles.actionIconPositive}`}>
        <CheckRoundedIcon fontSize="inherit" />
      </span>
      <span className={styles.actionIcon}>
        <CloseRoundedIcon fontSize="inherit" />
      </span>
      <span className={styles.actionIcon}>
        <OpenInNewRoundedIcon fontSize="inherit" />
      </span>
    </div>
  );
}

const CaseIntelligenceDashboard: React.FC<Props> = ({
  artefacts,
  claimantName = '',
  respondentName = '',
  tagSuggestions = null,
  tagSuggestionsPending = false,
  tagSuggestionsFeedback = null,
  onRetryTags = () => {},
  onAcceptTagSuggestion,
  onRejectTagSuggestion,
  onCitationClick,
  activeCitationSignature = null,
  activeTab: controlledActiveTab,
  onActiveTabChange,
  issueFilter: controlledIssueFilter,
  onIssueFilterChange,
}) => {
  const dashboard = buildDashboardModel(artefacts);
  const availableTabs = [...dashboard.tabs, TAGS_TAB];
  const [uncontrolledActiveTab, setUncontrolledActiveTab] = useState<DashboardUiTabId | null>(
    availableTabs[0]?.id || null,
  );
  const activeTabState = controlledActiveTab ?? uncontrolledActiveTab;
  const resolvedActiveTab = availableTabs.some((tab) => tab.id === activeTabState)
    ? activeTabState
    : availableTabs[0]?.id || null;
  const [uncontrolledIssueFilter, setUncontrolledIssueFilter] = useState<IssueFilter>('all');
  const resolvedIssueFilter = controlledIssueFilter ?? uncontrolledIssueFilter;
  const trimmedClaimantName = claimantName.trim();
  const trimmedRespondentName = respondentName.trim();
  const hasHeaderSubtitle = Boolean(trimmedClaimantName && trimmedRespondentName);

  useEffect(() => {
    if (controlledActiveTab == null && resolvedActiveTab !== uncontrolledActiveTab) {
      setUncontrolledActiveTab(resolvedActiveTab);
    }
  }, [controlledActiveTab, resolvedActiveTab, uncontrolledActiveTab]);

  const handleActiveTabChange = (nextTab: DashboardUiTabId) => {
    if (controlledActiveTab == null) {
      setUncontrolledActiveTab(nextTab);
    }
    onActiveTabChange?.(nextTab);
  };

  const handleIssueFilterChange = (nextFilter: IssueFilter) => {
    if (controlledIssueFilter == null) {
      setUncontrolledIssueFilter(nextFilter);
    }
    onIssueFilterChange?.(nextFilter);
  };

  return (
    <div className={styles.dashboard}>
      <div className={styles.shell}>
        <div className={styles.caseHeader}>
          <div className={styles.caseHeaderLabel}>Case Intelligence</div>
          {hasHeaderSubtitle ? (
            <div className={styles.caseHeaderSubtitle}>
              <span className={styles.partyNameGroup}>
                <span
                  className={`${styles.partyDot} ${styles.partyDotClaimant}`}
                  aria-hidden="true"
                />
                <span>{trimmedClaimantName}</span>
              </span>
              <span className={styles.partySeparator}> vs </span>
              <span className={styles.partyNameGroup}>
                <span
                  className={`${styles.partyDot} ${styles.partyDotRespondent}`}
                  aria-hidden="true"
                />
                <span>{trimmedRespondentName}</span>
              </span>
            </div>
          ) : null}
        </div>

        <div className={styles.tabList} role="tablist" aria-label="Case intelligence sections">
          {availableTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={resolvedActiveTab === tab.id}
              className={`${styles.tabButton} ${resolvedActiveTab === tab.id ? styles.tabButtonActive : ''}`}
              onClick={() => handleActiveTabChange(tab.id)}
            >
              <span>{tab.label}</span>
              {'badgeCount' in tab && tab.badgeCount ? (
                <span className={styles.tabBadge} aria-hidden="true">
                  {tab.badgeCount}
                </span>
              ) : null}
            </button>
          ))}
        </div>

        <div className={styles.panel}>
          {resolvedActiveTab === 'summary' ? (
            <div className={styles.summaryPane}>
              <div className={styles.summaryText}>
                {dashboard.overview.summary || 'No summary available.'}
              </div>

              <div className={styles.summaryGrid}>
                {dashboard.overview.summaryGroups.map((group) => (
                  <section key={group.id} className={styles.summaryCard}>
                    <div className={styles.sectionMetaRow}>
                      <div className={styles.sectionLabel}>
                        <span
                          className={`${styles.partyDot} ${
                            group.id === 'claimant'
                              ? styles.partyDotClaimant
                              : styles.partyDotRespondent
                          }`}
                          aria-hidden="true"
                        />
                        <span>{group.label}</span>
                      </div>
                      <div className={styles.sectionCount}>
                        {formatCount(
                          group.items.length,
                          group.itemLabel.slice(0, -1),
                          group.itemLabel,
                        )}
                      </div>
                    </div>

                    {group.items.length ? (
                      <ul className={styles.bulletList}>
                        {group.items.map((item, index) => (
                          <li key={`${item.text}-${index}`}>
                            {renderTextWithCitations(
                              item.text,
                              item.citations,
                              onCitationClick,
                              activeCitationSignature,
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className={styles.emptyStateText}>
                        Nothing surfaced for this side yet.
                      </div>
                    )}
                  </section>
                ))}
              </div>
            </div>
          ) : null}

          {resolvedActiveTab === 'theory' ? (
            <div className={styles.theoryPane}>
              {dashboard.overview.theories.length ? (
                dashboard.overview.theories.map((theory) => {
                  const theorySectionCitations = mergeArtefactCitations([
                    ...theory.citations,
                    ...theory.supportingAllegations.flatMap((item) => item.citations),
                    ...theory.weaknesses.flatMap((item) => item.citations),
                  ]);

                  return (
                    <section
                      key={`${theory.party}-${theory.title}`}
                      className={styles.theorySection}
                    >
                      <div className={styles.caseLabel}>Core theory</div>
                      <div className={styles.theoryTitleRow}>
                        <h2 className={styles.theoryTitle}>{theory.title}</h2>
                        <span className={styles.inlineBadge}>
                          <span
                            className={`${styles.partyDot} ${getPartyDotClass(theory.party, styles)}`}
                            aria-hidden="true"
                          />
                          <span>{theory.party}</span>
                        </span>
                      </div>
                      <p className={styles.theoryLead}>
                        {renderTextWithoutCitations(theory.coreProposition)}
                      </p>

                      {theory.supportingAllegations.length ? (
                        <div className={styles.contentBlock}>
                          <div className={styles.subheading}>Supporting allegations</div>
                          <ol className={styles.numberedList}>
                            {theory.supportingAllegations.map((item, index) => (
                              <li key={`${item.text}-${index}`}>
                                {renderTextWithoutCitations(item.text)}
                              </li>
                            ))}
                          </ol>
                        </div>
                      ) : null}

                      {theory.weaknesses.length ? (
                        <div className={styles.contentBlock}>
                          <div className={styles.subheading}>Weaknesses</div>
                          <ol className={styles.numberedList}>
                            {theory.weaknesses.map((item, index) => (
                              <li key={`${item.text}-${index}`}>
                                {renderTextWithoutCitations(item.text)}
                              </li>
                            ))}
                          </ol>
                        </div>
                      ) : null}

                      {renderSectionCitations(
                        theorySectionCitations,
                        onCitationClick,
                        activeCitationSignature,
                      )}
                    </section>
                  );
                })
              ) : (
                <div className={styles.emptyStateText}>No theory sections are available.</div>
              )}
            </div>
          ) : null}

          {resolvedActiveTab === 'issues' ? (
            <div className={styles.issuesPane}>
              <div className={styles.filterRow}>
                {ISSUE_FILTERS.map((filterOption) => (
                  <button
                    key={filterOption.id}
                    type="button"
                    className={`${styles.filterButton} ${
                      resolvedIssueFilter === filterOption.id ? styles.filterButtonActive : ''
                    }`}
                    onClick={() => handleIssueFilterChange(filterOption.id)}
                  >
                    {filterOption.label}
                  </button>
                ))}
              </div>

              {(resolvedIssueFilter === 'all' || resolvedIssueFilter === 'legal') &&
              dashboard.issues.data?.issues?.length ? (
                <section className={styles.sectionBlock}>
                  <div className={styles.sectionHeading}>Legal issues</div>
                  <div className={styles.issueCardList}>
                    {dashboard.issues.data.issues.map((issue) => (
                      <article key={issue.title} className={styles.issueCard}>
                        <div className={styles.issueMain}>
                          <div>
                            <h3 className={styles.cardTitle}>{issue.title}</h3>
                            {issue.description ? (
                              <p className={styles.cardDescription}>
                                {renderTextWithoutCitations(issue.description)}
                              </p>
                            ) : null}
                          </div>
                          <ReadOnlyActionIcons />
                        </div>

                        {issue.relatedEntities?.length ? (
                          <div className={styles.tagRow}>
                            {issue.relatedEntities.map((entity) => (
                              <span key={entity} className={styles.inlineTag}>
                                {entity}
                              </span>
                            ))}
                          </div>
                        ) : null}

                        {issue.claimantPosition || issue.defendantPosition ? (
                          <div className={styles.positionsGrid}>
                            {issue.claimantPosition ? (
                              <div className={styles.positionCard}>
                                <div className={styles.positionLabel}>Claimant position</div>
                                <p>{renderTextWithoutCitations(issue.claimantPosition)}</p>
                              </div>
                            ) : null}
                            {issue.defendantPosition ? (
                              <div className={styles.positionCard}>
                                <div className={styles.positionLabel}>Respondent position</div>
                                <p>{renderTextWithoutCitations(issue.defendantPosition)}</p>
                              </div>
                            ) : null}
                          </div>
                        ) : null}

                        {renderSectionCitations(
                          issue.citations,
                          onCitationClick,
                          activeCitationSignature,
                        )}
                      </article>
                    ))}
                  </div>
                </section>
              ) : null}

              {(resolvedIssueFilter === 'all' || resolvedIssueFilter === 'theme') &&
              dashboard.issues.themes.length ? (
                <section className={styles.sectionBlock}>
                  <div className={styles.sectionHeading}>Narrative themes</div>
                  <div className={styles.issueCardList}>
                    {dashboard.issues.themes.map((theme) => (
                      <article key={theme.title} className={styles.issueCard}>
                        <div className={styles.issueMain}>
                          <div>
                            <h3 className={styles.cardTitle}>{theme.title}</h3>
                            {theme.description ? (
                              <p className={styles.cardDescription}>{theme.description}</p>
                            ) : null}
                          </div>
                          <ReadOnlyActionIcons />
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              ) : null}

              {!dashboard.issues.data?.issues?.length && !dashboard.issues.themes.length ? (
                <div className={styles.emptyStateText}>No issues or themes are available.</div>
              ) : null}
            </div>
          ) : null}

          {resolvedActiveTab === 'parties' ? (
            <div className={styles.partiesPane}>
              <div className={styles.partyColumns}>
                {dashboard.parties.groups.map((group) => (
                  <section key={group.id} className={styles.partyColumn}>
                    <div className={styles.columnLabel}>
                      <span
                        className={`${styles.columnDot} ${getColumnDotClass(group.id, styles)}`}
                      />
                      {group.label}
                    </div>
                    <div className={styles.partyCardList}>
                      {group.items.map((party) => (
                        <article key={party.name} className={styles.partyCard}>
                          <h3 className={styles.cardTitle}>{party.name}</h3>
                          {formatRoleLine(party.role, party.type) ? (
                            <div className={styles.secondaryLine}>
                              {formatRoleLine(party.role, party.type)}
                            </div>
                          ) : null}
                          {party.description ? (
                            <p className={styles.cardDescription}>
                              {renderTextWithCitations(
                                party.description,
                                party.citations,
                                onCitationClick,
                                activeCitationSignature,
                              )}
                            </p>
                          ) : null}
                          {party.aliases?.length ? (
                            <div className={styles.metaLine}>
                              Aliases: {party.aliases.join(', ')}
                            </div>
                          ) : null}
                          {formatMentionCount(party.mentionCount) ? (
                            <div className={styles.metaLine}>
                              {formatMentionCount(party.mentionCount)}
                            </div>
                          ) : null}
                        </article>
                      ))}
                    </div>
                  </section>
                ))}
              </div>

              {dashboard.parties.data?.relationships?.length ? (
                <section className={styles.secondarySection}>
                  <div className={styles.sectionHeading}>Relationships</div>
                  <div className={styles.tableWrap}>
                    <table className={styles.dataTable}>
                      <thead>
                        <tr>
                          <th>From</th>
                          <th>Relationship</th>
                          <th>To</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboard.parties.data.relationships.map((relationship) => (
                          <tr
                            key={`${relationship.from}-${relationship.relationship}-${relationship.to}`}
                          >
                            <td>{relationship.from}</td>
                            <td>{relationship.relationship}</td>
                            <td>{relationship.to}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ) : null}
            </div>
          ) : null}

          {resolvedActiveTab === 'evidence' ? (
            <div className={styles.evidencePane}>
              <div className={styles.evidenceGrid}>
                <section className={styles.evidencePanel}>
                  <div className={styles.sectionHeading}>Supporting evidence</div>
                  {dashboard.evidence.supporting.items.length ? (
                    <ol className={`${styles.stackList} ${styles.stackNumberedList}`}>
                      {dashboard.evidence.supporting.items.map((item, index) => (
                        <li key={`${item.text}-${index}`} className={styles.stackItem}>
                          {renderTextWithoutCitations(item.text)}
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <div className={styles.emptyStateText}>No supporting evidence is listed.</div>
                  )}
                  {renderSectionCitations(
                    mergeArtefactCitations(
                      dashboard.evidence.supporting.items.flatMap((item) => item.citations),
                    ),
                    onCitationClick,
                    activeCitationSignature,
                  )}
                </section>

                <section className={styles.evidencePanel}>
                  <div className={styles.sectionHeading}>Conflicting evidence</div>
                  {dashboard.evidence.conflicting.items.length ? (
                    <ol className={`${styles.stackList} ${styles.stackNumberedList}`}>
                      {dashboard.evidence.conflicting.items.map((item, index) => (
                        <li key={`${item.text}-${index}`} className={styles.stackItem}>
                          {renderTextWithoutCitations(item.text)}
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <div className={styles.emptyStateText}>No conflicting evidence is listed.</div>
                  )}
                  {renderSectionCitations(
                    mergeArtefactCitations(
                      dashboard.evidence.conflicting.items.flatMap((item) => item.citations),
                    ),
                    onCitationClick,
                    activeCitationSignature,
                  )}
                </section>
              </div>

              <section className={styles.secondarySection}>
                <div className={styles.sectionHeading}>Evidential gaps</div>
                {dashboard.evidence.gaps.items.length ? (
                  <div className={styles.gapGrid}>
                    {dashboard.evidence.gaps.items.map((item, index) => (
                      <div key={`${item.text}-${index}`} className={styles.gapCell}>
                        {renderTextWithCitations(
                          item.text,
                          item.citations,
                          onCitationClick,
                          activeCitationSignature,
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={styles.emptyStateText}>No evidential gaps are listed.</div>
                )}
              </section>
            </div>
          ) : null}

          {resolvedActiveTab === 'timeline' ? (
            <div className={styles.timelinePane}>
              {dashboard.timeline.items.length ? (
                <div className={styles.timelineList}>
                  {dashboard.timeline.items.map((item) => (
                    <article key={`${item.date}-${item.title}`} className={styles.timelineItem}>
                      <div className={styles.timelineMarker} />
                      <div className={styles.timelineBody}>
                        <div className={styles.timelineDate}>{formatDateLabel(item.date)}</div>
                        <div className={styles.timelineTitle}>{item.title}</div>
                        {buildTimelineDescription(item) ? (
                          <div className={styles.timelineDescription}>
                            {renderTextWithCitations(
                              buildTimelineDescription(item),
                              item.citations,
                              onCitationClick,
                              activeCitationSignature,
                            )}
                          </div>
                        ) : null}
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className={styles.emptyStateText}>No timeline entries are available.</div>
              )}
            </div>
          ) : null}

          {resolvedActiveTab === 'action-items' ? (
            <div className={styles.actionsPane}>
              <section className={styles.guidanceCard}>
                <div className={styles.sectionMetaRow}>
                  <div>
                    <div className={styles.cardTitle}>Key guidance</div>
                    <div className={styles.supportingCopy}>
                      Priority actions for upcoming preparation
                    </div>
                  </div>
                  <span className={styles.inlineBadge}>Priority</span>
                </div>

                {dashboard.questions.guidanceItems.length ? (
                  <ol className={styles.numberedList}>
                    {dashboard.questions.guidanceItems.map((item, index) => (
                      <li key={`${item.text}-${index}`}>
                        {renderTextWithCitations(
                          item.text,
                          item.citations,
                          onCitationClick,
                          activeCitationSignature,
                        )}
                      </li>
                    ))}
                  </ol>
                ) : (
                  <div className={styles.emptyStateText}>No guidance is available.</div>
                )}
              </section>

              <section className={styles.secondarySection}>
                <div className={styles.sectionHeading}>Next call focus</div>
                {dashboard.questions.orderedQuestions.length ? (
                  <div className={styles.questionList}>
                    {dashboard.questions.orderedQuestions.map((question, index) => (
                      <article
                        key={`${question.question}-${index}`}
                        className={styles.questionCard}
                      >
                        <div className={styles.tagRow}>
                          <span
                            className={`${styles.priorityTag} ${styles[`priority${PRIORITY_LABELS[question.priority]}`]}`}
                          >
                            {PRIORITY_LABELS[question.priority]}
                          </span>
                          {question.category ? (
                            <span className={styles.inlineTag}>
                              {humanizeToken(question.category)}
                            </span>
                          ) : null}
                        </div>
                        <div className={styles.questionTitle}>{question.question}</div>
                        {question.suggestedAction ? (
                          <div className={styles.supportingCopy}>
                            {renderTextWithCitations(
                              question.suggestedAction,
                              question.citations,
                              onCitationClick,
                              activeCitationSignature,
                            )}
                          </div>
                        ) : null}
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className={styles.emptyStateText}>No action items are available.</div>
                )}
              </section>
            </div>
          ) : null}

          {resolvedActiveTab === 'glossary' ? (
            <div className={styles.glossaryPane}>
              {dashboard.glossary.data?.terms?.length ? (
                <div className={styles.glossaryGrid}>
                  {dashboard.glossary.data.terms.map((term) => (
                    <article key={term.term} className={styles.glossaryCard}>
                      <h3 className={styles.cardTitle}>{term.term}</h3>
                      <p className={styles.cardDescription}>
                        {renderTextWithCitations(
                          term.definition,
                          term.citations,
                          onCitationClick,
                          activeCitationSignature,
                        )}
                      </p>
                      {term.aliases?.length ? (
                        <div className={styles.metaLine}>Aliases: {term.aliases.join(', ')}</div>
                      ) : null}
                    </article>
                  ))}
                </div>
              ) : (
                <div className={styles.emptyStateText}>No glossary terms are available.</div>
              )}

              {dashboard.glossary.data?.abbreviations?.length ? (
                <section className={styles.secondarySection}>
                  <div className={styles.sectionHeading}>Abbreviations</div>
                  <div className={styles.tableWrap}>
                    <table className={styles.dataTable}>
                      <thead>
                        <tr>
                          <th>Abbreviation</th>
                          <th>Expansion</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboard.glossary.data.abbreviations.map((abbreviation) => (
                          <tr key={abbreviation.abbreviation}>
                            <td>{abbreviation.abbreviation}</td>
                            <td>{abbreviation.expansion}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ) : null}
            </div>
          ) : null}

          {resolvedActiveTab === 'tags' ? (
            <TagSuggestionsPanel
              tagSuggestions={tagSuggestions}
              tagSuggestionsPending={tagSuggestionsPending}
              tagSuggestionsFeedback={tagSuggestionsFeedback}
              onRetry={onRetryTags}
              onAcceptSuggestion={onAcceptTagSuggestion}
              onRejectSuggestion={onRejectTagSuggestion}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default CaseIntelligenceDashboard;
